import { PEOPLE_SEARCH_CATEGORIES } from "./types.js";
/**
 * Path of the public coverage-count endpoint, relative to an app-api origin.
 * Apps that talk to a per-environment API join this to their own base URL.
 */
export const PUBLIC_STATS_PATH = "/v3/public/companies/stats";
/**
 * Production origin. The counts are the same in every environment and the
 * endpoint is unauthenticated, so build-time callers (Astro) read production
 * directly rather than whichever API their deploy lane happens to point at.
 */
export const PUBLIC_STATS_ENDPOINT = `https://api.privacybee.com${PUBLIC_STATS_PATH}`;
/**
 * The counts endpoint on the API an app is configured against.
 *
 * `PUBLIC_STATS_PATH` is absolute FROM THE API ORIGIN, and the bases the apps
 * carry are two different shapes: admin, hive and checkout ship
 * `https://api.privacybee.com/v3` and write request paths relative to it, while
 * partners and auth ship the bare origin and put `/v3` on every path. Joining
 * the path onto the base by concatenation is therefore right for half of them
 * and `.../v3/v3/public/companies/stats` for the other half — a 404 that
 * `loadPublicStats` absorbs into the committed snapshot, so every count in three
 * apps quietly ages instead of failing. Resolving as a URL reads the origin and
 * discards whatever path the base carries, which is one answer for both shapes.
 *
 * A base that is not a URL is a broken deploy config rather than a reason to
 * render nothing, so it says so and reads production — the same answer no base
 * at all gets.
 */
export function publicStatsEndpoint(apiBase) {
    if (!apiBase)
        return PUBLIC_STATS_ENDPOINT;
    try {
        return new URL(PUBLIC_STATS_PATH, apiBase).toString();
    }
    catch {
        console.warn(`public stats: "${apiBase}" is not a URL, so the counts endpoint cannot be derived from it — ` +
            `reading ${PUBLIC_STATS_ENDPOINT} instead.`);
        return PUBLIC_STATS_ENDPOINT;
    }
}
/**
 * A count that has stalled past this has no chance of beating the render it is
 * meant to improve — the committed snapshot is already on screen, so waiting
 * longer buys nothing and holds a build (or a page) open.
 */
export const PUBLIC_STATS_TIMEOUT_MS = 5_000;
function isPositiveInteger(value) {
    return typeof value === "number" && Number.isInteger(value) && value > 0;
}
/**
 * A category with no brokers in it yet is a real answer, not a broken one — the
 * catalogue creates a type before the first company lands in it, and a payload
 * carrying `{ industry_optout: 0 }` is describing coverage accurately. Rejecting
 * it would throw away the whole live answer over one honest zero and downgrade
 * every app to the committed snapshot, so a category count only has to be a
 * whole number that is not negative.
 */
function isCount(value) {
    return typeof value === "number" && Number.isInteger(value) && value >= 0;
}
/**
 * How far the breakdown may sum PAST the total before the payload is refused.
 *
 * The endpoint models the categories as a partition — today's capture sums to
 * exactly the total — so a sum past it means either a broker counted under two
 * types or a total that has not caught up with its own rows. Either way the
 * plan tables would render category chips that add up to more coverage than the
 * total beside them.
 *
 * A zero allowance would be a trap, and the headroom is exactly zero: ONE broker
 * legitimately typed into two categories would refuse every live payload, and
 * because `refresh-snapshot.mjs` reads the same validator it would also leave
 * the committed figure unrefreshable while `check-freshness.mjs` fails it on the
 * calendar. So there is an allowance, and it is the same one percent of the
 * broker count that `check-freshness.mjs` treats as the point where an
 * over-claim stops being defensible — eleven brokers at 1,116. Below it, a real
 * overlap costs nothing; above it, the breakdown has come apart and needs a
 * decision rather than a publish.
 */
const CATEGORY_OVERLAP_ALLOWANCE = 0.01;
/** The most the breakdown may exceed the total it breaks down. */
export function maxCategoryOverlap(total) {
    return Math.ceil(total * CATEGORY_OVERLAP_ALLOWANCE);
}
/** Whether a breakdown claims more coverage than the total it breaks down. */
export function breakdownOverClaims(stats) {
    const summed = Object.values(stats.data_broker_types).reduce((total, count) => total + count, 0);
    return summed - stats.data_brokers > maxCategoryOverlap(stats.data_brokers);
}
/**
 * Read the live counts once. Throws on anything short of a complete, plausible
 * payload — a partial answer must never reach a page, because these numbers are
 * published verbatim and a zero, a missing category or a renamed one renders as
 * a claim.
 *
 * ONE validator, so the figure that ships and the figure it falls back to are
 * held to the same bar: `scripts/refresh-snapshot.mjs` writes the committed
 * snapshot from this function's return, so a payload the live path refuses is a
 * payload the fallback can never be written from either.
 *
 * Callers wanting the fail-soft behaviour want `loadPublicStats`.
 */
export async function fetchPublicStats(endpoint = PUBLIC_STATS_ENDPOINT) {
    const response = await fetch(endpoint, {
        headers: { accept: "application/json" },
        signal: AbortSignal.timeout(PUBLIC_STATS_TIMEOUT_MS),
    });
    if (!response.ok) {
        throw new Error(`public stats: ${endpoint} answered ${response.status}`);
    }
    const body = (await response.json());
    const stats = body?.data;
    if (!isPositiveInteger(stats?.data_brokers) || !isPositiveInteger(stats?.custom_sites)) {
        throw new Error(`public stats: ${endpoint} returned no usable counts`);
    }
    const types = stats.data_broker_types;
    if (!types || typeof types !== "object" || Array.isArray(types)) {
        throw new Error(`public stats: ${endpoint} returned no category breakdown`);
    }
    const entries = Object.entries(types);
    // At least one category has to carry brokers. A breakdown that is entirely
    // zeroes is not an empty category, it is an empty answer.
    if (!entries.some(([, count]) => isPositiveInteger(count)) || !entries.every(([, count]) => isCount(count))) {
        throw new Error(`public stats: ${endpoint} returned an unusable category breakdown`);
    }
    // A shape that does not carry the keys we read is not a valid payload. Without
    // this a renamed slug validates, `peopleSearchCount` answers 0, and a page
    // publishes "0 People Search Sites" — a wrong figure rather than a fallback.
    const missing = PEOPLE_SEARCH_CATEGORIES.filter((slug) => !isCount(types[slug]));
    if (missing.length > 0) {
        throw new Error(`public stats: ${endpoint} returned a category breakdown with no ${missing.map((slug) => `"${slug}"`).join(" or ")} — ` +
            "the people-search count reads those slugs by name and would publish 0");
    }
    const live = {
        data_brokers: stats.data_brokers,
        custom_sites: stats.custom_sites,
        data_broker_types: Object.fromEntries(entries),
    };
    if (breakdownOverClaims(live)) {
        throw new Error(`public stats: ${endpoint} returned a category breakdown that claims more coverage than the ` +
            `${live.data_brokers} it breaks down. Either a broker is counted in two categories or the total is ` +
            "stale — settle it with the endpoint's owner before publishing either figure.");
    }
    return live;
}
